import React, {useEffect, useState} from 'react'
import QuestionToolbar from '../../../components/screens/surveyForms/questions/QuestionToolbar'
import QuestionCanvas from '../../../components/screens/surveyForms/questions/QuestionCanvas'
import QuestionPreview from '../../../components/screens/surveyForms/questions/QuestionPreview'
import SurveySettings from './SurveySettings'
import QuestionLibrary from './QuestionLibrary'
import QuestionsList from './QuestionsList'
import AddedQuestions from './AddedQuestions'
import {useParams} from 'react-router-dom'
import {Backdrop, CircularProgress, Fab} from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import {surveysApi} from '../../../services'
import {AUDIENCE, ITEM_TYPE} from '../../../utils/constants'
import Themes from './Themes'
import themesApi from '../../../services/api/themes/themeApi.js'
import {loadBuilderItems} from './loadBuilderItems'
import {emptyContentScreen} from '../../../utils/contentScreen'
import {emptyRules} from '../../../utils/skipLogic/rules'
import {isContentScreen, isSkipLogicSection} from '../../../utils/surveyItems'
import ContentScreenRender from '../../../components/screens/surveyForms/items/ContentScreenRender'
import SurveyPreview from '../../SurveyView/SurveyPreview'

export default function QuestionPanel() {
  const {token} = useParams()

  const [loading, setLoading] = useState(false)
  const [loadingTheme, setLoadingTheme] = useState(false)
  const [loadingQuestions, setLoadingQuestions] = useState(false)
  const [view, setView] = useState('main')
  const [index, setIndex] = useState(null)
  const [survey, setSurvey] = useState(null)
  const [questions, setQuestions] = useState([])
  const [theme, setTheme] = useState(null)
  const [preview, setPreview] = useState(false)

  const handleAddNewQuestion = val => {
    setIndex(questions?.length)

    val.audiences = survey.audience
    setQuestions([
      ...questions,
      {
        ...val,
        order: questions?.length - 1,
        questionSettings: {...val.questionSettings},
        sliderResponseAttributes: {...val.sliderResponseAttributes},
        multiOptionResponseAttributes: val?.multiOptionResponseAttributes
          ? [...val.multiOptionResponseAttributes]
          : null,
        followupQuestions: val.followupQuestions
          ? [...val.followupQuestions]
          : null,
        openResponseAttributes: {...val.openResponseAttributes},
      },
    ])
  }
  const handleAddContentScreen = () => {
    setIndex(questions?.length)
    setQuestions([
      ...questions,
      {itemType: ITEM_TYPE.CONTENT, itemId: null, content: emptyContentScreen()},
    ])
  }
  const handleAddSkipLogicSection = () => {
    setIndex(questions?.length)
    setQuestions([
      ...questions,
      {itemType: ITEM_TYPE.SKIP_LOGIC, itemId: null, rules: emptyRules()},
    ])
  }
  useEffect(() => {
    setLoading(true)
    setLoadingQuestions(true)
    surveysApi
      .get(token)
      .then(res => {
        setSurvey({...res})
        setLoadingTheme(true)
        themesApi
          .getSurveyThemes(res.id, res.schoolId, res.matId)
          .then(t => {
            if (t) {
              setTheme({...t})
            }
          })
          .catch(err => {
            console.log(err)
          })
          .finally(() => setLoadingTheme(false))
        // The item endpoint is keyed by survey id, so load items once the
        // survey has resolved.
        return loadBuilderItems(res.id, token)
      })
      .then(res => {
        let qs = []
        if (res && res.length > 0) {
          qs = [
            ...res.map(q => {
              if (isContentScreen(q) || isSkipLogicSection(q)) return q
              const categoryId =
                q.categoryId ?? q.categoryID ?? q.CategoryId ?? null
              const categoryName =
                q.categoryName ??
                q.categoryname ??
                q.CategoryName ??
                q.category?.name ??
                null
              return {
                ...q,
                categoryId,
                categoryName,
                questionSettings: {...q.questionSettings},
                sliderResponseAttributes: {...q.sliderResponseAttributes},
                multiOptionResponseAttributes: [
                  ...q.multiOptionResponseAttributes,
                ],
                followupQuestions: [...q.followupQuestions],
                openResponseAttributes: {...q.openResponseAttributes},
              }
            }),
          ]
        }
        setQuestions(qs)
      })
      .catch(err => console.log(err))
      .finally(() => {
        setLoading(false)
        setLoadingQuestions(false)
      })
  }, [])

  const checkUnsavedChanges = () => {
    if (
      questions.filter(
        q =>
          q.id === 0 ||
          (isContentScreen(q) && !q.itemId) ||
          (isSkipLogicSection(q) && !q.itemId)
      ).length > 0
    ) {
      return true
    }
    return false
  }

  const getAudience = audience => {
    switch (audience) {
      case AUDIENCE.STAFF + AUDIENCE.PARENT:
        return [AUDIENCE.STAFF, AUDIENCE.PARENT]
      case AUDIENCE.STAFF + AUDIENCE.STUDENT:
        return [AUDIENCE.STAFF, AUDIENCE.STUDENT]
      case AUDIENCE.PARENT + AUDIENCE.STUDENT:
        return [AUDIENCE.PARENT, AUDIENCE.STUDENT]
      case AUDIENCE.STAFF + AUDIENCE.PARENT + AUDIENCE.STUDENT:
        return [AUDIENCE.STAFF, AUDIENCE.PARENT, AUDIENCE.STUDENT]
      default:
        return [audience]
    }
  }
  return loading || loadingQuestions || loadingTheme ? (
    <Backdrop
      open={loading || loadingQuestions || loadingTheme}
      sx={theme => ({color: '#fff', zIndex: theme.zIndex.drawer + 1})}
    >
      <CircularProgress color="inherit" />
    </Backdrop>
  ) : (
    <div className="question-panels">
      <QuestionToolbar
        view={view}
        setView={setView}
        onScreenChange={checkUnsavedChanges}
      ></QuestionToolbar>
      <QuestionCanvas>
        {view === 'library' || view === 'inventory' ? (
          <QuestionLibrary
            survey={survey}
            view={view}
            questions={questions}
            setQuestions={setQuestions}
          ></QuestionLibrary>
        ) : view === 'settings' ? (
          <SurveySettings
            survey={survey}
            setSurvey={setSurvey}
          ></SurveySettings>
        ) : view === 'themes' ? (
          <Themes
            survey={survey}
            surveyTheme={theme}
            setTheme={setTheme}
          ></Themes>
        ) : (
          <>
            <QuestionsList
              survey={survey}
              questions={questions}
              onQuestionChange={questions => setQuestions([...questions])}
              index={index}
              setIndex={setIndex}
              priorityAudience={
                survey?.audience ? getAudience(survey?.audience) : [1]
              }
            ></QuestionsList>
            <AddedQuestions
              onChange={handleAddNewQuestion}
              onAddContentScreen={handleAddContentScreen}
              onAddSkipLogicSection={handleAddSkipLogicSection}
            ></AddedQuestions>
          </>
        )}
      </QuestionCanvas>
      {isContentScreen(questions?.[index]) ? (
        <div className="question-preview grow">
          <div
            className="preview"
            style={{backgroundColor: theme?.backgroundColor}}
          >
            <div className="question-box">
              <ContentScreenRender content={questions[index].content} />
            </div>
          </div>
        </div>
      ) : isSkipLogicSection(questions?.[index]) ? (
        <div className="question-preview grow">
          <div
            className="preview"
            style={{backgroundColor: theme?.backgroundColor}}
          >
            <div className="question-box">
              <p className="skip-logic-preview-note">
                Skip-logic sections are not shown to respondents. They decide
                where people go next, based on earlier answers.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <QuestionPreview
          tabs={survey?.audience ? getAudience(survey?.audience) : [1]}
          hasQuestion={questions?.length > 0}
          question={
            questions
              ? {
                  ...questions[index],
                }
              : null
          }
          theme={theme}
        ></QuestionPreview>
      )}
      {view === 'main' && questions?.length > 0 && (
        <Fab
          variant="extended"
          onClick={() => setPreview(true)}
          sx={{position: 'fixed', bottom: 24, right: 24, zIndex: 1200}}
        >
          <PlayArrowIcon sx={{mr: 1}} />
          Preview
        </Fab>
      )}
      {preview && (
        <SurveyPreview
          entries={questions}
          audience={
            survey?.audience ? getAudience(survey.audience)[0] : AUDIENCE.STAFF
          }
          surveyName={survey?.name}
          theme={theme}
          onExit={() => setPreview(false)}
        />
      )}
    </div>
  )
}
